
import React, { useState, useEffect } from "react";
import { format, parse } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

interface DatePickerFieldProps {
  id: string;
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
}

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder = "Select date",
  error,
  required = false,
}) => {
  // Format date as string for input field (use local format)
  const formatDateForInput = (date: Date | null): string => {
    return date ? format(date, "yyyy-MM-dd") : "";
  };

  // Parse date from string input
  const parseDateFromInput = (dateString: string): Date | null => {
    if (!dateString) return null;
    
    try {
      // Try to parse using the HTML input date format (yyyy-MM-dd)
      const parsedDate = parse(dateString, "yyyy-MM-dd", new Date());
      
      // Validate the parsed date
      if (isNaN(parsedDate.getTime())) {
        return null;
      }
      
      return parsedDate;
    } catch (error) {
      console.error("Failed to parse date:", error);
      return null;
    }
  };

  // State to track the input value
  const [dateInputValue, setDateInputValue] = useState<string>(
    formatDateForInput(value)
  );

  // Handle manual date input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDateInputValue(inputValue);
    
    // Try to parse the date from input
    const parsedDate = parseDateFromInput(inputValue);
    if (parsedDate) {
      onChange(parsedDate);
    } else if (!inputValue) {
      // Clear the date if input is empty
      onChange(null);
    }
  };

  // Update input value when the calendar selection changes
  useEffect(() => {
    setDateInputValue(formatDateForInput(value));
  }, [value]);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      
      <div className="flex space-x-2">
        <Input
          id={id}
          type="date"
          value={dateInputValue}
          onChange={handleInputChange}
          className={cn(
            "flex-1",
            error && "border-red-500"
          )}
        />
        
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "px-3",
                error && "border-red-500"
              )}
              type="button" // Prevent form submission
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={value || undefined}
              onSelect={onChange}
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>
      
      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  );
};

export default DatePickerField;
